import { foldersService, type LibraryFolder } from "@/services/foldersService";

/** Collect unique folder paths and parent paths from Drive file paths. */
export function extractFolderPathsFromDrivePaths(
  drivePaths: Array<string | undefined>
): string[] {
  const folderPaths = new Set<string>();

  for (const raw of drivePaths) {
    const folderPath = raw?.trim();
    if (!folderPath) continue;
    folderPaths.add(folderPath);
    const parts = folderPath.split("/");
    for (let i = 1; i < parts.length; i++) {
      folderPaths.add(parts.slice(0, i).join("/"));
    }
  }

  return Array.from(folderPaths).sort((a, b) => {
    const depthA = a.split("/").length;
    const depthB = b.split("/").length;
    return depthA - depthB;
  });
}

/**
 * Create library folders mirroring Drive paths. Returns map of drive path → folder id.
 */
export async function createDriveFolderMirror(
  folderPaths: string[],
  workspaceId: string | null,
  existingFolders?: LibraryFolder[]
): Promise<Map<string, string>> {
  const folderMap = new Map<string, string>();
  const siblings = existingFolders ?? (await foldersService.getFolders(workspaceId));

  for (const folderPath of folderPaths) {
    const pathParts = folderPath.split("/");
    const folderName = pathParts[pathParts.length - 1];
    const parentPath =
      pathParts.length > 1 ? pathParts.slice(0, -1).join("/") : null;
    const parentFolderId = parentPath ? folderMap.get(parentPath) || null : null;

    const existingFolder = siblings.find(
      (f) =>
        f.name.toLowerCase() === folderName.toLowerCase() &&
        (f.parentId === parentFolderId || (!f.parentId && !parentFolderId))
    );

    if (existingFolder) {
      folderMap.set(folderPath, existingFolder.id);
    } else {
      const newFolder = await foldersService.createFolder(
        { name: folderName, parentId: parentFolderId },
        workspaceId
      );
      folderMap.set(folderPath, newFolder.id);
      siblings.push(newFolder);
    }
  }

  return folderMap;
}
